import { useMutation } from 'react-query';

import { useAuth0 } from '@auth0/auth0-react';

import { exchangeAuth0Token } from 'apis/AuthAPI';
import { useAppDispatch } from 'store/hook';
import { handleLogin } from 'store/slices/authSlice';

/**
 * Hook for exchanging Auth0 ID token for PA JWT tokens.
 *
 * After successful Auth0 authentication, this hook:
 * 1. Gets the Auth0 ID token using getIdTokenClaims()
 * 2. Sends it to the backend /api/jwt/auth/auth0-exchange/ endpoint
 * 3. Dispatches handleLogin with the returned PA tokens
 *
 * Note: We use the ID token (not access token) because Auth0 returns opaque
 * access tokens unless an API audience is configured. The ID token is always a JWT.
 */
export const useAuth0TokenExchange = () => {
  const { getIdTokenClaims, logout: auth0Logout } = useAuth0();
  const dispatch = useAppDispatch();

  const { mutateAsync: exchangeToken, isLoading, error } = useMutation(
    async () => {
      const claims = await getIdTokenClaims();
      // __raw is a documented property on IdToken containing the raw JWT string
      // See: https://auth0.github.io/auth0-spa-js/interfaces/IdToken.html
      if (!claims?.__raw) {
        throw new Error('Failed to get ID token from Auth0');
      }
      return exchangeAuth0Token(claims.__raw);
    },
    {
      onSuccess: (response) => {
        dispatch(handleLogin({
          currentUser: response.userInfo,
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
        }));
      },
      onError: () => {
        auth0Logout({ logoutParams: { returnTo: window.location.origin + '/sign-in' } });
      },
    }
  );

  const errorMessage = error
    ? (error as any)?.response?.data?.detail || (error as Error)?.message || 'Authentication failed. Please try again.'
    : null;

  return { exchangeToken, isLoading, error: errorMessage };
};
